import * as React from 'react';
import { FixMeLater } from '../../../utils';
import { Typography, TypographyBaseProps } from '../Typography';
import { useComponentDefaultProps, TypographyBodySize } from '../../../theming';
import { useStyles } from './Anchor.styles';
import { AnchorUnderline } from './Anchor.types';
import { ButtonBase } from '../../ButtonBase/ButtonBase';

export interface AnchorProps extends TypographyBaseProps {
    size?: TypographyBodySize | 'inherit';

    /**
     * If `true`, a pseudo element will be rendered which will fill the closest relative parent.
     * This can be used to make a link cover a parent component, making the entire surface clickable, while still allowing other foreground links to be rendered in front of it.
     * @default false
     */
    blockLink?: boolean;

    /**
     * The root element.
     * @default a
     */
    component?: React.ElementType;

    /**
     * Control when the link will be underlined.
     * @default 'always'
     */
    underline?: AnchorUnderline;
}

const defaultProps: Partial<AnchorProps> = {
    size: 'inherit',
    component: 'a',
    underline: 'always',
    blockLink: false,
};

export const Anchor: React.ForwardRefExoticComponent<AnchorProps & React.RefAttributes<HTMLElement>> = React.forwardRef(
    function Anchor(props: AnchorProps, ref: React.ForwardedRef<FixMeLater>): React.JSX.Element {
        const {
            size = 'inherit',
            blockLink = false,
            underline = 'always',
            component: componentProp,
            ...other
        } = useComponentDefaultProps('Anchor', defaultProps, props);

        const { styles } = useStyles({ underline });

        const component = componentProp === 'button' ? ButtonBase : componentProp;

        return (
            <Typography
                ref={ref}
                component={component}
                css={styles.root}
                data-block-link={blockLink || undefined}
                family="body"
                size={size}
                {...other}
            />
        );
    }
);
